import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useScroll, useSpring, useTransform } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import './History.css';
import { Reveal, SplitText, useMediaQuery } from './motion';
import { COMPANY, TIMELINE_YEARS } from '../data/site';

// Na desktopu se časová osa posouvá vodorovně podle vertikálního scrollu
// (sekce se „připne“ a obsah jede do strany). Na mobilu je to klasický svislý seznam.
const History = () => {
  const { t, i18n } = useTranslation();
  const events = t('history.events', { returnObjects: true });
  const isDesktop = useMediaQuery('(min-width: 960px) and (prefers-reduced-motion: no-preference)');

  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const distance = useMotionValue(0);
  const [height, setHeight] = useState(null);

  useLayoutEffect(() => {
    if (!isDesktop || !trackRef.current) {
      setHeight(null);
      return;
    }
    const measure = () => {
      const d = Math.max(0, trackRef.current.scrollWidth - window.innerWidth);
      distance.set(d);
      setHeight(window.innerHeight + d);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(trackRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isDesktop, distance, i18n.language]);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });
  const x = useTransform([scrollYProgress, distance], ([p, d]) => -p * d);
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  const intro = (
    <div className="history__intro">
      <SplitText text={t('history.title')} className="section-head__title" />
      <Reveal className="history__body" delay={0.1}>
        <p>
          <Trans i18nKey="history.p1" values={{ founded: COMPANY.founded }} />
        </p>
        <p>{t('history.p2')}</p>
        <p>
          <Trans i18nKey="history.p3" />
        </p>
      </Reveal>
      {isDesktop && (
        <p className="history__hint">
          {t('history.hint')} <span aria-hidden="true">→</span>
        </p>
      )}
    </div>
  );

  return (
    <section
      id="history"
      ref={sectionRef}
      className={`history section--dark ${isDesktop ? 'history--horizontal' : 'section'}`}
      style={isDesktop && height ? { height } : undefined}
    >
      <div className="history__sticky">
        <motion.div ref={trackRef} className="history__track" style={isDesktop ? { x } : undefined}>
          {intro}
          <ol className="history__events">
            {events.map((text, i) => (
              <motion.li
                key={i}
                className="history__event"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.8, delay: isDesktop ? 0 : 0.05 }}
              >
                <span className="history__year">{TIMELINE_YEARS[i]}</span>
                <span className="history__dot" aria-hidden="true" />
                <p className="history__text">{text}</p>
              </motion.li>
            ))}
          </ol>
        </motion.div>

        {isDesktop && (
          <div className="history__progress container" aria-hidden="true">
            <span>{TIMELINE_YEARS[0]}</span>
            <div className="history__bar">
              <motion.i style={{ scaleX: progress }} />
            </div>
            <span>{TIMELINE_YEARS[TIMELINE_YEARS.length - 1]}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default History;
